import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api';
import { useLanguage } from '../../i18n/LanguageContext';
import './Dashboard.css';

const Dashboard = () => {
    const { user } = useAuth();
    const { t } = useLanguage();
    const [stats, setStats] = useState({
        members: { total: 0, active: 0, suspended: 0, retired: 0 },
        loans: { total_loans: 0, active_loans: 0, total_disbursed: 0, total_outstanding: 0 },
        contributions: { monthly: [], total: 0 },
        communications: { recent: [], stats: {} }
    });
    const [loading, setLoading] = useState(true);

    // Member-specific state
    const [memberProfile, setMemberProfile] = useState(null);
    const [memberContribs, setMemberContribs] = useState([]);
    const [memberLoans, setMemberLoans] = useState([]);
    const [memberNotifications, setMemberNotifications] = useState([]);
    const [memberUpcoming, setMemberUpcoming] = useState(null);

    useEffect(() => {
        if (user?.role !== 'member') {
            loadDashboardData();
        }
        if (user?.role === 'member') loadMemberData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadDashboardData = async () => {
        setLoading(true);
        try {
            const [membersRes, loansRes, contributionsRes, commsRes] = await Promise.all([
                api.getMemberDashboardStats(),
                api.getLoansSummary(),
                api.getMonthlyContributionsSummary(new Date().getFullYear()),
                api.getRecentAnnouncements()
            ]);

            setStats({
                members: membersRes.data,
                loans: loansRes.data,
                contributions: contributionsRes.data,
                communications: { recent: commsRes.data, stats: {} }
            });
        } catch (error) {
            console.error('Error loading dashboard data:', error);
            // If token is invalid, redirect to login
            if (error.message && error.message.includes('Invalid token')) {
                localStorage.removeItem('tarewu_token');
                localStorage.removeItem('tarewu_user');
                window.location.href = '/login';
            }
        } finally {
            setLoading(false);
        }
    };

    const loadMemberData = async () => {
        setLoading(true);
        try {
            const me = await api.getMyMember();
            const mp = me.data?.member || me.data || null;
            setMemberProfile(mp);

            if (mp && mp.id) {
                try {
                    const contribs = await api.getMemberContributions(mp.id);
                    setMemberContribs(contribs.data?.contributions || []);
                } catch (e) {
                    console.error('Failed to load contributions:', e);
                    setMemberContribs([]);
                }

                try {
                    const loans = await api.getLoans({ member_id: mp.id });
                    setMemberLoans(loans.data || loans || []);
                } catch (e) {
                    console.error('Failed to load loans:', e);
                    setMemberLoans([]);
                }

                try {
                    const meetings = await api.getMeetings({ upcoming: true });
                    setMemberUpcoming((meetings.data || meetings || [])[0] || null);
                } catch (e) {
                    console.error('Failed to load meetings:', e);
                    setMemberUpcoming(null);
                }

                try {
                    const comms = await api.getRecentAnnouncements();
                    setMemberNotifications(comms.data || comms || []);
                } catch (e) {
                    console.error('Failed to load notifications:', e);
                    setMemberNotifications([]);
                }
            }
        } catch (err) {
            console.error('Failed to load member dashboard data', err);
            // If token is invalid, redirect to login
            if (err.message && err.message.includes('Invalid token')) {
                localStorage.removeItem('tarewu_token');
                localStorage.removeItem('tarewu_user');
                window.location.href = '/login';
            }
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-TZ', {
            style: 'currency',
            currency: 'TZS',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const formatNumber = (num) => {
        return new Intl.NumberFormat('en').format(num);
    };

    const getMonthName = (month) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return months[month - 1] || '';
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            active: 'badge-success',
            pending: 'badge-warning',
            completed: 'badge-info',
            suspended: 'badge-danger',
            rejected: 'badge-secondary',
            draft: 'badge-secondary'
        };
        return statusMap[status] || 'badge-secondary';
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        const parts = name.split(' ');
        return parts.map(p => p[0]).join('').toUpperCase().slice(0, 2);
    };

    if (loading) {
        return (
            <div className="loading">
                <div className="loading-spinner"></div>
            </div>
        );
    }

    // MEMBER-SPECIFIC DASHBOARD
    if (user?.role === 'member') {
        // If no member profile, show message
        if (!memberProfile) {
            return (
                <div className="dashboard member-dashboard">
                    <div className="dashboard-header">
                        <div className="welcome-section">
                            <h1 className="welcome-title">{t('Karibu,')} {user?.name || t('Mwanachama')}!</h1>
                            <p className="welcome-subtitle">{t('Hii ni ukurasa wako wa Mwanachama.')}</p>
                        </div>
                    </div>
                    <div className="empty-state">
                        <h3>{t('Hakuna wasifu wa mwanachama')}</h3>
                        <p>{t('Wasifu wako wa mwanachama haujapatikana. Tafadhali wasiliana na ofisi kuu kwa msaada.')}</p>
                    </div>
                </div>
            );
        }

        const monthlyContribution = (() => {
            // Find most recent contribution (fallback to 0)
            const contribs = Array.isArray(memberContribs) ? memberContribs : [];
            if (contribs.length === 0) return { total: 0, salary: 0, rate: 4 };
            
            // Sort by period year and month to get the most recent
            const sorted = [...contribs].sort((a, b) => {
                const yearDiff = (b.period_year || 0) - (a.period_year || 0);
                if (yearDiff !== 0) return yearDiff;
                return (b.period_month || 0) - (a.period_month || 0);
            });
            
            const latest = sorted[0];
            return {
                total: latest ? (latest.total_amount || latest.amount || 0) : 0,
                salary: latest ? (latest.salary_used || 0) : 0,
                rate: latest ? (latest.deduction_rate || 4) : 4
            };
        })();

        const totalOutstandingLoan = (() => {
            const loans = Array.isArray(memberLoans) ? memberLoans : [];
            // Get loans that are active/ongoing
            const activeLoans = loans.filter(l => 
                l.loan_status === 'active' || 
                l.loan_status === 'approved' || 
                l.status === 'active'
            );
            return activeLoans.reduce((acc, l) => acc + (parseFloat(l.outstanding_balance) || parseFloat(l.balance_remaining) || parseFloat(l.total_outstanding) || 0), 0);
        })();

        const nextInstallment = (() => {
            const loans = Array.isArray(memberLoans) ? memberLoans : [];
            const activeLoans = loans.filter(l => 
                l.loan_status === 'active' || 
                l.loan_status === 'approved' || 
                l.status === 'active'
            );
            if (activeLoans.length > 0) {
                return activeLoans[0].next_payment_date || activeLoans[0].next_installment_date || '-';
            }
            return '-';
        })();

        return (
            <div className="dashboard member-dashboard">
                <div className="dashboard-header">
                    <div className="welcome-section">
                        <h1 className="welcome-title">{t('Karibu,')} {user?.name || t('Mwanachama')}!</h1>
                        <p className="welcome-subtitle">{t('Hii ni ukurasa wako wa Mwanachama — maelezo muhimu haraka.')}</p>
                    </div>
                    <div className="profile-card">
                        <div className="profile-avatar">{user?.name?.split(' ').map(n=>n[0]).join('').slice(0,2)}</div>
                        <div className="profile-info">
                            <h3 className="profile-name">{user?.name}</h3>
                            <p className="profile-role">{memberProfile?.membership_status || t('Unknown')}</p>
                            <p className="profile-email">{memberProfile?.member_number || ''}</p>
                        </div>
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="stats-grid member-stats-grid">
                    <div className="stat-card">
                        <div className="member-stat-top"><span className="member-stat-icon">👥</span><span className="stat-label">{t('Hali ya Uanachama')}</span></div>
                        <div className="stat-value">{(memberProfile && (memberProfile.membership_status || memberProfile.status)) || t('Inactive')}</div>
                    </div>

                    <div className="stat-card">
                        <div className="member-stat-top"><span className="member-stat-icon">💰</span><span className="stat-label">{t('Mchango wa Hivi Karibuni')}</span></div>
                        <div className="stat-value">{formatCurrency(monthlyContribution.total)}</div>
                        <div className="stat-sub">{t('Msho:')} {formatCurrency(monthlyContribution.salary)} • {monthlyContribution.rate}%</div>
                    </div>

                    <div className="stat-card">
                        <div className="member-stat-top"><span className="member-stat-icon">🏦</span><span className="stat-label">{t('Mkopo Unaobaki')}</span></div>
                        <div className="stat-value">{formatCurrency(totalOutstandingLoan)}</div>
                        <div className="stat-sub">{t('Next installment:')} {nextInstallment}</div>
                    </div>

                    <div className="stat-card">
                        <div className="member-stat-top"><span className="member-stat-icon">📅</span><span className="stat-label">{t('Tukio Linalokuja')}</span></div>
                        <div className="stat-value">{memberUpcoming?.title || t('Hakuna tukio')}</div>
                        <div className="stat-sub">{memberUpcoming?.date || memberUpcoming?.start_date || ''}</div>
                    </div>

                    <div className="stat-card">
                        <div className="member-stat-top"><span className="member-stat-icon">🔔</span><span className="stat-label">{t('Notifications mpya')}</span></div>
                        <div className="stat-value">{(Array.isArray(memberNotifications) ? memberNotifications : []).length}</div>
                        <div className="stat-sub">{t('Kumbuka kuangalia ujumbe na mikutano')}</div>
                    </div>
                </div>

                {/* Module grid (Module kwa Module) */}
                <div className="quick-actions">
                    <h3 className="section-title">{t('Huduma Zangu')}</h3>
                    <div className="actions-grid member-modules-grid">
                        <a href="/profile" className="action-card">
                            <div className="action-icon">👤</div>
                            <div className="action-content">
                                <h4>{t('Usimamizi wa Wanachama')}</h4>
                                <p>{t('View & edit profile, download ID (QR)')}</p>
                            </div>
                        </a>

                        <a href="/contributions" className="action-card">
                            <div className="action-icon">💰</div>
                            <div className="action-content">
                                <h4>{t('Michango')}</h4>
                                <p>{t('View history, pay manually if payroll missed')}</p>
                            </div>
                        </a>

                        <a href="/loans" className="action-card">
                            <div className="action-icon">🏦</div>
                            <div className="action-content">
                                <h4>{t('Mikopo')}</h4>
                                <p>{t('View loans, repayment schedule & status')}</p>
                            </div>
                        </a>

                        <a href="/communications" className="action-card">
                            <div className="action-icon">📢</div>
                            <div className="action-content">
                                <h4>{t('Mawasiliano')}</h4>
                                <p>{t('Announcements, SMS history & meeting notices')}</p>
                            </div>
                        </a>

                        <a href="/meetings" className="action-card">
                            <div className="action-icon">📅</div>
                            <div className="action-content">
                                <h4>{t('Mikutano')}</h4>
                                <p>{t('Agenda, minutes, vote (if open)')}</p>
                            </div>
                        </a>

                        <a href="/trainings" className="action-card">
                            <div className="action-icon">🎓</div>
                            <div className="action-content">
                                <h4>{t('Mafunzo')}</h4>
                                <p>{t('Upcoming trainings, register & download certificates')}</p>
                            </div>
                        </a>

                        <a href="/elections" className="action-card">
                            <div className="action-icon">🗳️</div>
                            <div className="action-content">
                                <h4>{t('Uchaguzi')}</h4>
                                <p>{t('View candidates, vote when election is open')}</p>
                            </div>
                        </a>

                        <a href="/my-id" className="action-card">
                            <div className="action-icon">🆔</div>
                            <div className="action-content">
                                <h4>{t('ID ya Kidigitali')}</h4>
                                <p>{t('View QR code, membership number & payment history')}</p>
                            </div>
                        </a>

                        <a href="/cases" className="action-card">
                            <div className="action-icon">⚖</div>
                            <div className="action-content">
                                <h4>{t('Migogoro')}</h4>
                                <p>{t('Report a case, upload docs and track status')}</p>
                            </div>
                        </a>

                        <a href="/library" className="action-card">
                            <div className="action-icon">📚</div>
                            <div className="action-content">
                                <h4>{t('Elimu & Library')}</h4>
                                <p>{t('Read policies, download PDFs')}</p>
                            </div>
                        </a>

                        <a href="/support" className="action-card">
                            <div className="action-icon">🛠</div>
                            <div className="action-content">
                                <h4>{t('Helpdesk')}</h4>
                                <p>{t('Submit a ticket & track progress')}</p>
                            </div>
                        </a>

                        <a href="/calendar" className="action-card">
                            <div className="action-icon">📆</div>
                            <div className="action-content">
                                <h4>{t('Kalenda')}</h4>
                                <p>{t('Events, trainings, elections & meetings')}</p>
                            </div>
                        </a>
                    </div>
                </div>

                {/* Small detail sections: recent contributions, loans, notifications */}
                <div className="dashboard-grid">
                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title">{t('Michango (History)')}</h3>
                            <div className="card-actions">
                                <a className="link" href="/contributions">{t('View all')}</a>
                            </div>
                        </div>
                        <div className="card-body">
                            {(Array.isArray(memberContribs) ? memberContribs : []).slice(0,5).map((c, i) => (
                                <div key={i} className="list-row">
                                    <div>{c.period || `${c.period_month || '-'} / ${c.period_year || '-'}`}</div>
                                    <div>{formatCurrency(c.amount || c.total_amount || 0)}</div>
                                    <div className="muted">{c.status || t('Paid')}</div>
                                </div>
                            ))}
                            {(Array.isArray(memberContribs) ? memberContribs : []).length === 0 && <div className="empty-note">{t('No contributions yet.')}</div>}
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title">{t('Mikopo')}</h3>
                            <div className="card-actions">
                                <a className="link" href="/loans">{t('View all')}</a>
                            </div>
                        </div>
                        <div className="card-body">
                            {(Array.isArray(memberLoans) ? memberLoans : []).slice(0,3).map((l, idx) => (
                                <div key={idx} className="loan-row">
                                    <div className="loan-title">{l.name || `${t('Loan')} ${l.id || idx+1}`}</div>
                                    <div className="loan-amount">{formatCurrency(l.amount || l.principal || 0)}</div>
                                    <div className="loan-balance">{formatCurrency(l.outstanding_balance || l.balance_remaining || 0)}</div>
                                    <div className="loan-status">{l.status || t('Active')}</div>
                                </div>
                            ))}
                            {(Array.isArray(memberLoans) ? memberLoans : []).length === 0 && <div className="empty-note">{t('No loans found.')}</div>}
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title">{t('Notifications')}</h3>
                            <div className="card-actions">
                                <a className="link" href="/communications">{t('View all')}</a>
                            </div>
                        </div>
                        <div className="card-body">
                            {(Array.isArray(memberNotifications) ? memberNotifications : []).slice(0,5).map(n => (
                                <div key={n.id || n.title} className="notification-item">
                                    <div className="notification-title">{n.title || n.message}</div>
                                    <div className="notification-meta muted">{new Date(n.sent_at || n.created_at || Date.now()).toLocaleDateString()}</div>
                                </div>
                            ))}
                            {(Array.isArray(memberNotifications) ? memberNotifications : []).length === 0 && <div className="empty-note">{t('No notifications')}</div>}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard staff-dashboard">
            {/* Welcome Header with Profile */}
            <div className="dashboard-header">
                <div className="welcome-section">
                    <span className="dashboard-kicker">TAREWU • OPERATIONS OVERVIEW</span>
                    <h1 className="welcome-title">{t('Welcome back,')} {user?.name || t('User')}!</h1>
                    <p className="welcome-subtitle">{t("Here's what's happening with your union today.")}</p>
                </div>
                <div className="profile-card">
                    <div className="profile-avatar">
                        {user?.name ? getInitials(user.name) : 'U'}
                    </div>
                    <div className="profile-info">
                        <h3 className="profile-name">{user?.name || 'User'}</h3>
                        <p className="profile-role">{user?.role || t('Member')}</p>
                        <p className="profile-email">{user?.email || '-'}</p>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="stats-grid">
                <div className="stat-card stat-members">
                    <div className="metric-top"><span className="metric-code">01</span><span className="metric-state">{t('Members')}</span></div>
                    <div className="stat-value">{formatNumber(stats.members.total_members || 0)}</div>
                    <div className="stat-label">{t('Total Members')}</div>
                    <div className="metric-footer"><span><i className="dot active" />{formatNumber(stats.members.active_members || 0)} {t('Active')}</span><span><i className="dot suspended" />{formatNumber(stats.members.suspended || 0)} {t('Suspended')}</span></div>
                </div>

                <div className="stat-card stat-loans">
                    <div className="metric-top"><span className="metric-code">02</span><span className="metric-state">{t('Loans')}</span></div>
                    <div className="stat-value">{formatNumber(stats.loans.active_loans || 0)}</div>
                    <div className="stat-label">{t('Active Loans')}</div>
                    <div className="metric-footer"><span>{formatNumber(stats.loans.total_loans || 0)} {t('Total loan records')}</span></div>
                </div>

                <div className="stat-card stat-contributions">
                    <div className="metric-top"><span className="metric-code">03</span><span className="metric-state">{t('Finance')}</span></div>
                    <div className="stat-value">{formatCurrency(stats.loans.total_disbursed || 0)}</div>
                    <div className="stat-label">{t('Total Disbursed')}</div>
                    <div className="metric-footer"><span>{formatCurrency(stats.loans.total_outstanding || 0)} {t('Outstanding')}</span></div>
                </div>

                <div className="stat-card stat-pending">
                    <div className="metric-top"><span className="metric-code">04</span><span className="metric-state">{t('Review queue')}</span></div>
                    <div className="stat-value">{formatNumber(stats.loans.pending_loans || 0)}</div>
                    <div className="stat-label">{t('Pending Applications')}</div>
                    <div className="metric-footer"><Link to="/loans" className="stat-action-link">{t('Open loan applications')} <span>→</span></Link></div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="quick-actions">
                <h3 className="section-title">{t('Quick Actions')}</h3>
                <div className="actions-grid">
                    <Link to="/members/new" className="action-card">
                        <div className="action-icon">👤</div>
                        <div className="action-content">
                            <h4>{t('Add New Member')}</h4>
                            <p>{t('Register a new member to the union')}</p>
                        </div>
                    </Link>
                    <Link to="/contributions" className="action-card">
                        <div className="action-icon">💵</div>
                        <div className="action-content">
                            <h4>{t('Record Contribution')}</h4>
                            <p>{t('Add new member contribution')}</p>
                        </div>
                    </Link>
                    <Link to="/loans" className="action-card">
                        <div className="action-icon">📝</div>
                        <div className="action-content">
                            <h4>{t('Loan Application')}</h4>
                            <p>{t('Process new loan request')}</p>
                        </div>
                    </Link>
                    <Link to="/communications" className="action-card">
                        <div className="action-icon">📢</div>
                        <div className="action-content">
                            <h4>{t('Send Announcement')}</h4>
                            <p>{t('Broadcast message to members')}</p>
                        </div>
                    </Link>
                </div>
            </div>

            {/* Main Content Grid */}
            <div className="dashboard-grid">
                {/* Contributions Chart */}
                <div className="card chart-card">
                    <div className="card-header">
                        <h3 className="card-title">{t('Monthly Contributions')}</h3>
                        <select className="chart-period-select">
                            <option>2024</option>
                        </select>
                    </div>
                    <div className="contributions-chart">
                        {Array.isArray(stats.contributions.monthly) && stats.contributions.monthly.length > 0 ? (
                            <div className="chart-bars">
                                {stats.contributions.monthly.map((item, index) => (
                                    <div key={index} className="chart-bar-container">
                                        <div 
                                            className="chart-bar" 
                                            style={{ 
                                                height: `${(item.total_amount / Math.max(...stats.contributions.monthly.map(i => i.total_amount || 1))) * 100}%` 
                                            }}
                                        >
                                            <span className="chart-value">{formatCurrency(item.total_amount || 0)}</span>
                                        </div>
                                        <span className="chart-label">{getMonthName(item.period_month)}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="empty-chart">
                                <div className="empty-icon">📊</div>
                                <p>{t('No contribution data available')}</p>
                                <span>{t('Start recording contributions to see analytics')}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Recent Announcements */}
                <div className="card">
                    <div className="card-header">
                        <h3 className="card-title">{t('Recent Announcements')}</h3>
                        <Link to="/communications" className="view-all-link">
                            {t('View All →')}
                        </Link>
                    </div>
                    <div className="announcements-list">
                        {Array.isArray(stats.communications.recent) && stats.communications.recent.length > 0 ? (
                            stats.communications.recent.slice(0, 5).map((item) => (
                                <div key={item.id} className="announcement-item">
                                    <div className="announcement-header">
                                        <span className={`badge ${getStatusBadge(item.status)}`}>
                                            {item.message_type || t('General')}
                                        </span>
                                        <span className="announcement-date">
                                            {new Date(item.sent_at || item.created_at).toLocaleDateString('en-TZ')}
                                        </span>
                                    </div>
                                    <h4 className="announcement-title">{item.title}</h4>
                                    <p className="announcement-preview">
                                        {item.message ? (item.message.length > 80 ? item.message.substring(0, 80) + '...' : item.message) : t('No preview available')}
                                    </p>
                                </div>
                            ))
                        ) : (
                            <div className="empty-list">
                                <div className="empty-icon">📭</div>
                                <p>{t('No announcements yet')}</p>
                                <Link to="/communications" className="create-link">{t('Create your first announcement')}</Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Quick Stats Footer */}
            <div className="quick-stats-footer">
                <div className="quick-stat">
                    <div className="quick-stat-icon">📈</div>
                    <div className="quick-stat-content">
                        <span className="quick-stat-value">{formatNumber(stats.members.retired || 0)}</span>
                        <span className="quick-stat-label">{t('Retired Members')}</span>
                    </div>
                </div>
                <div className="quick-stat">
                    <div className="quick-stat-icon">✅</div>
                    <div className="quick-stat-content">
                        <span className="quick-stat-value">{formatNumber(stats.members.active_members || 0)}</span>
                        <span className="quick-stat-label">{t('Active This Month')}</span>
                    </div>
                </div>
                <div className="quick-stat">
                    <div className="quick-stat-icon">💼</div>
                    <div className="quick-stat-content">
                        <span className="quick-stat-value">{formatCurrency(stats.loans.total_outstanding || 0)}</span>
                        <span className="quick-stat-label">{t('Outstanding Loans')}</span>
                    </div>
                </div>
                <div className="quick-stat">
                    <div className="quick-stat-icon">🎯</div>
                    <div className="quick-stat-content">
                        <span className="quick-stat-value">{formatNumber(stats.loans.active_loans || 0)}</span>
                        <span className="quick-stat-label">{t('Active Loan Accounts')}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
